import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";
import Loading from "../../../../../Utils/Loading/Loading";
import Backend from "../../../../../Backend/Backend";

export default function Deposit({ onClose }) {
  const [backend, setBackend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noBank, setNoBank] = useState(false);

  const [utr, setUtr] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [uploadImage, setUploadImage] = useState(null);
  const [bankData, setBankData] = useState({});
  const [step, setStep] = useState(1);

  const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  // =============================
  // Init Backend
  // =============================
  useEffect(() => {
    const initBackend = async () => {
      const b = await new Backend().init();
      setBackend(b);
    };
    initBackend();
  }, []);

  // =============================
  // Load Bank Data
  // =============================
  const loadBankData = useCallback(async () => {
    if (!backend) return;

    setLoading(true);

    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: localStorage.getItem("id"),
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
    };

    try {
      const r = await backend.bank_account(data);

      if (r.error === "False") {
        if (!r.account || Object.keys(r.account).length === 0) {
          setNoBank(true);
          setBankData({});
        } else {
          setNoBank(false);
          setBankData(r.account);
        }
      } else {
        if (r.logout === 1) window.LogOut?.();
        else toast.error(r.message);
      }
    } catch {
      toast.error("Network Error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [backend]);

  useEffect(() => {
    loadBankData();
  }, [loadBankData]);

  // =============================
  // Upload Handler
  // =============================
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images allowed!");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File must be under 2MB");
      return;
    }

    setUploadImage(file);
  };

  // =============================
  // Submit
  // =============================
  const handleSubmit = async () => {
    const amt = Number(depositAmount);

    if (!Number.isFinite(amt) || amt < 500) {
      toast.error("Minimum deposit amount is ₹500");
      return;
    }

    if (!uploadImage) {
      toast.error("Please upload payment proof");
      return;
    }

    if (!bankData?.id) {
      toast.error("Invalid bank configuration");
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        token: localStorage.getItem("token"),
        username: localStorage.getItem("username"),
        amount: amt,
        utr: utr,
        bank_id: bankData.id,
      };

      const formData = new FormData();
      formData.append("bank_id", bankData.id);
      formData.append("amount", amt);
      formData.append("utr", utr || "");
      formData.append("file", uploadImage);

      const r = await backend.upload_payment_amount_ss(formData, data);

      if (r.error === "False") {
        toast.success(r.message);
        setDepositAmount("");
        setUploadImage(null);
        setUtr("");
        setStep(1);
        onClose();
      } else {
        toast.error(r.message);
      }
    } catch {
      toast.error("Network error. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // Loading
  // =============================
  if (loading || !backend) return <Loading />;

  // =============================
  // No Bank
  // =============================
  if (noBank) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-md bg-[var(--background)] rounded-3xl shadow-2xl p-8 text-center border border-[var(--hover)]">
          <h3 className="text-xl font-semibold text-[var(--sell)] mb-4">
            No Active Bank Account Found
          </h3>
          <p className="text-[var(--text)] mb-6">
            Please contact administrator.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-xl bg-[var(--primary)] text-white"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // =============================
  // MAIN MODAL
  // =============================
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-4xl bg-[var(--background)] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-[var(--hover)] max-h-[90vh] flex flex-col">

        {/* HEADER */}
        <div className="flex justify-between items-center p-6 border-b border-[var(--hover)]">
          <h3 className="text-xl font-semibold text-[var(--text)]">
            Deposit Funds
          </h3>
          <button onClick={onClose} className="text-xl text-[var(--text)]">
            ✕
          </button>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="overflow-y-auto p-6">

          {/* STEP INDICATOR */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-4 text-sm font-semibold">
              <div className={`px-4 py-1 rounded-full ${step === 1 ? "bg-[var(--primary)] text-white" : "bg-[var(--hover)] text-[var(--text)]"}`}>
                1. Amount
              </div>
              <div className="h-[2px] w-10 bg-[var(--border)]"></div>
              <div className={`px-4 py-1 rounded-full ${step === 2 ? "bg-[var(--primary)] text-white" : "bg-[var(--hover)] text-[var(--text)]"}`}>
                2. Payment
              </div>
            </div>
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="flex justify-center">
              <div className="w-full max-w-md bg-[var(--hover)] rounded-2xl p-6 space-y-6">
                <label className="text-sm font-semibold text-[var(--text)]">
                  Enter Deposit Amount
                </label>

                <input
                  type="number"
                  placeholder="Minimum 500"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--hover)] bg-[var(--background)] text-[var(--text)] focus:ring-2 focus:ring-[var(--primary)] outline-none"
                />

                <button
                  onClick={() => {
                    if (Number(depositAmount) < 500) {
                      toast.error("Minimum deposit ₹500");
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-[var(--primary)] to-indigo-600 text-white font-semibold shadow-lg"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* LEFT */}
              <div className="space-y-6">

                <div className="bg-gradient-to-r from-[var(--primary)] to-indigo-500 text-white rounded-2xl p-6 text-center shadow-lg">
                  <p className="text-sm opacity-80">Deposit Amount</p>
                  <p className="text-3xl font-bold mt-1">
                    ₹{Number(depositAmount).toLocaleString("en-IN")}
                  </p>
                </div>

                <div className="bg-[var(--hover)] rounded-2xl p-6 space-y-3 border border-[var(--hover)] text-[var(--text)]">
                  <p className="font-semibold">Bank Details</p>

                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span>Account Name</span>
                      <span>{bankData.account_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Account Number</span>
                      <span>{bankData.account_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IFSC</span>
                      <span>{bankData.account_ifsc}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Bank</span>
                      <span>{bankData.bank}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setStep(1)}
                  className="w-full py-2 rounded-xl border border-[var(--hover)] text-[var(--text)]"
                >
                  Back
                </button>
              </div>

              {/* RIGHT */}
              <div className="space-y-6">

                {bankData?.qr_name && (
                  <div className="flex flex-col items-center text-[var(--text)]">
                    <p className="font-semibold mb-4">Scan QR</p>
                    <div className="bg-white p-6 rounded-2xl shadow-md">
                      <QRCodeSVG value={bankData.qr_name} size={180} />
                    </div>
                  </div>
                )}

                <label className="flex flex-col items-center justify-center border-2 border-dashed border-[var(--hover)] rounded-2xl p-6 cursor-pointer hover:border-[var(--primary)] transition text-[var(--text)]">
                  <p className="text-sm mb-2">
                    Click to upload payment screenshot
                  </p>
                  <p className="text-xs">JPG / PNG (Max 2MB)</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>

                {uploadImage && (
                  <p className="text-xs text-[var(--buy)]">
                    ✔ {uploadImage.name}
                  </p>
                )}

                <input
                  type="text"
                  placeholder="Enter UTR ID"
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--hover)] bg-[var(--background)] text-[var(--text)] focus:ring-2 focus:ring-[var(--primary)] outline-none"
                />

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-[var(--primary)] to-indigo-600 text-white font-semibold shadow-lg hover:opacity-90 transition disabled:opacity-60"
                >
                  Submit Deposit Request
                </button>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}